import { useEffect, useRef, useState } from 'react'
import { cn } from '@neo-agents/ui'

interface AttachmentImageProps {
  src: string
  mimeType: string
  alt: string
  className?: string
}

export function AttachmentImage({ src, mimeType, alt, className }: AttachmentImageProps) {
  const [resolvedSrc, setResolvedSrc] = useState<string | null>(null)
  const [fallbackObjectUrl, setFallbackObjectUrl] = useState<string | null>(null)
  const [shouldLoad, setShouldLoad] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const node = containerRef.current
    if (!node) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry?.isIntersecting) return
        setShouldLoad(true)
        observer.disconnect()
      },
      {
        // Start fetching slightly before the image enters the viewport so the
        // newest visible messages feel responsive while older off-screen images
        // do not compete for bandwidth.
        rootMargin: '240px 0px',
      },
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!shouldLoad) return

    let cancelled = false

    async function loadAttachmentImage() {
      try {
        const response = await fetch(src, {
          credentials: 'include',
          referrerPolicy: 'no-referrer',
          headers: {
            'Content-Disposition': 'inline',
          },
        })
        const rawBlob = await response.blob()
        const normalizedBlob =
          rawBlob.type === mimeType
            ? rawBlob
            : new Blob([rawBlob], { type: mimeType })
        const nextObjectUrl = URL.createObjectURL(normalizedBlob)
        if (cancelled) {
          URL.revokeObjectURL(nextObjectUrl)
          return
        }
        setFallbackObjectUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev)
          return nextObjectUrl
        })
        setResolvedSrc(nextObjectUrl)
      } catch {
        if (!cancelled) {
          setResolvedSrc(src)
        }
      }
    }

    void loadAttachmentImage()

    return () => {
      cancelled = true
    }
  }, [mimeType, shouldLoad, src])

  useEffect(() => {
    return () => {
      if (fallbackObjectUrl) {
        URL.revokeObjectURL(fallbackObjectUrl)
      }
    }
  }, [fallbackObjectUrl])

  if (!resolvedSrc) {
    return (
      <div
        ref={containerRef}
        className={cn(
          className,
          // Keep a measurable box before the image loads so IntersectionObserver
          // can actually intersect off-screen history items.
          'min-h-24 min-w-24 bg-foreground/5',
        )}
      />
    )
  }

  return (
    <div ref={containerRef}>
      <img
        src={resolvedSrc}
        alt={alt}
        className={className}
        loading="lazy"
        decoding="async"
      />
    </div>
  )
}
